import { IElement, PageModel, PanelModel } from '@psyomics/surveyjs-core';

import { useRenderContext } from '../../../context/render-context';
import { type Survey } from '../../../survey-types';
import * as contentUtils from '../../../utils/content';
import * as createBlockUtils from '../../../utils/create-block';
import { ResolveFields } from '../../../utils/create-block';
import * as linkUtils from '../../../utils/links';
import * as fieldUtils from '../../../utils/surveys/fields';
import * as surveyUtils from '../../../utils/surveys/surveys';

import { QuestionAnswerBlock } from './question-and-answer-block';

interface IQuestionAnswerBlock {
  readonly answersOnly?: boolean;
  readonly truncateTo?: number;
  readonly displayQuestionNumbers?: boolean;
  readonly displayAnswerValues?: boolean;
  readonly excludedQuestions?: { question: string }[];
  readonly selectedQuestions?: { question: string }[];
  readonly link?: string;
  readonly externalLink?: string;
}

function selectElements(survey: Survey, selected?: { question: string }[]): IElement[] | undefined {
  if (!selected || selected.length === 0) return undefined;
  return selected.map((q) => surveyUtils.getQuestionByName(survey, q.question)).filter(Boolean);
}

function filterExcludedQuestions(
  elements: IElement[] | undefined,
  excludedQuestions?: { question: string }[]
): IElement[] | undefined {
  if (!elements?.length || !excludedQuestions?.length) return elements;
  const excludeSet = new Set(excludedQuestions.map((q) => q.question));
  return elements.filter((el) => el?.name && !excludeSet.has(el.name));
}

function resolveElements(params: {
  survey: Survey;
  page: PageModel;
  panel?: PanelModel;
  selectedQuestions?: { question: string }[];
  excludedQuestions?: { question: string }[];
}): IElement[] | undefined {
  const { survey, page, panel, selectedQuestions, excludedQuestions } = params;
  const selected = selectElements(survey, selectedQuestions);
  let elements: IElement[] | undefined;
  if (selected?.length) {
    elements = selected;
  } else if (panel) {
    const { elements: panelElements } = panel;
    elements = panelElements;
  } else {
    const pageQuestions = typeof page.getQuestions === 'function' ? page.getQuestions(true) : page.elements;
    elements = pageQuestions;
  }
  elements = filterExcludedQuestions(elements, excludedQuestions);
  return elements;
}

const QuestionsAndAnswers = ({
  answersOnly,
  displayQuestionNumbers,
  displayAnswerValues,
  truncateTo,
  selectedQuestions,
  excludedQuestions,
  link,
  externalLink,
}: IQuestionAnswerBlock) => {
  const { survey, answers, page, panel } = useRenderContext();

  if (!survey) return <div>No survey</div>;
  if (!answers) return <div>No answers</div>;
  if (!page) return <div>No page selected.</div>;

  const elements = resolveElements({ survey, page, panel, selectedQuestions, excludedQuestions });
  if (!elements?.length) return <div>No elements found.</div>;

  return (
    <QuestionAnswerBlock
      answers={answers}
      answersOnly={answersOnly}
      displayAnswerValues={displayAnswerValues}
      displayQuestionNumbers={displayQuestionNumbers}
      elements={elements}
      externalLink={externalLink}
      link={link}
      respondentType={survey?.respondentType}
      truncateTo={Number(truncateTo)}
    />
  );
};

const booleanFieldProps = {
  type: 'radio' as const,
  options: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ],
};

const resolveFields: ResolveFields<IQuestionAnswerBlock> = (
  { props: { survey, page, panel, excludedQuestions, selectedQuestions, link } },
  { fields, appState }
) => {
  const allLinks = contentUtils.getAllAnchors(appState.data.content);

  return {
    ...fields,
    ...(survey && {
      ...(!selectedQuestions?.length && {
        excludedQuestions: fieldUtils.createQuestionArraySelectField({ survey, page, panel }, 'Exclude Questions'),
      }),
      ...(!excludedQuestions?.length && {
        selectedQuestions: fieldUtils.createQuestionArraySelectField({ survey, page, panel }),
      }),
    }),
    answersOnly: {
      label: 'Answers only',
      options: [
        {
          label: 'Questions and Answers',
          value: false,
        },
        {
          label: 'Answers only',
          value: true,
        },
      ],
      type: 'radio',
    },
    truncateTo: {
      label: 'Truncate answers to n characters',
      type: 'number',
    },
    ...linkUtils.createLinkFields(allLinks, link === linkUtils.EXTERNAL_OPTION_VALUE),
    displayAnswerValues: {
      label: 'Display raw answer values',
      ...booleanFieldProps,
    },
    displayQuestionNumbers: {
      label: 'Display question numbers',
      ...booleanFieldProps,
    },
  };
};

const createQuestionAnswerBlock = createBlockUtils.makeBlockFactory<IQuestionAnswerBlock>(
  { withExampleAnswersJson: true, withPageSelect: true },
  {
    label: 'Questions and answers',
    render: QuestionsAndAnswers,
    resolveFields,
  }
);

export { createQuestionAnswerBlock, type IQuestionAnswerBlock, QuestionsAndAnswers, resolveFields };
